import React, { useState, useEffect, useMemo } from 'react'

import { DndContext, useSensor, useSensors, MouseSensor, closestCenter } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import { ButtonIcon, Col, Row } from 'twyne-new.ui'

const sortableItemStyle = (transform, transition, isDragging) => ({
  transform: CSS.Transform.toString(transform),
  transition,
  opacity: isDragging ? 0.8 : 1,
  zIndex: isDragging ? 1 : 0,
  position: 'relative',
  boxSizing: 'border-box',
  maxWidth: '100%',
})

export const DragHandle = ({ className }) => <>
  <span className={className || ''}>
    <GripVertical className='hover:twy-text-base-accent' size={16} />
  </span>
</>

export const SortableFieldsContainer = ({ children, onSortEnd, ...props }) => {
  const [items, setItems] = useState([])
  const sensors = useSensors(useSensor(MouseSensor, {}))

  useEffect(() => {
    if (children) {
      const ids = React.Children.map(children, child => child?.props?.index?.toString() || '')
      setItems(ids.filter(Boolean))
    }
  }, [children])

  const handleDragEnd = event => {
    const { active, over } = event

    if (active && over && active.id !== over.id) {
      const oldIndex = items.indexOf(active.id)
      const newIndex = items.indexOf(over.id)

      if (onSortEnd) {
        onSortEnd({ oldIndex, newIndex })
      }
    }
  }

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div {...props}>
          {children}
        </div>
      </SortableContext>
    </DndContext>
  )
}

export const SortableField = ({ children, index, customDrag, removeColContainer }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: index.toString(),
    attributes: {
      role: 'button',
      tabIndex: 0,
    },
  })

  const style = sortableItemStyle(transform, transition, isDragging)

  const dragHandleElement = customDrag ? null : <Col size="1" className='flex justify-center pt-2'>
    <div {...attributes} {...listeners}>
      <DragHandle />
    </div>
  </Col>

  return <Row ref={setNodeRef} style={style}>
    {!customDrag && dragHandleElement}
    {removeColContainer ? children : <Col size={11}>{children}</Col>}
  </Row>
}

export const DndSortableList = ({
  items,
  itemIds,
  onSortEnd,
  children,
  collisionDetection = closestCenter,
  activationConstraint,
}) => {
  const ids = useMemo(() => itemIds || items.map(item => String(item.id)), [items, itemIds])
  const sensors = useSensors(
    useSensor(MouseSensor, activationConstraint != null ? { activationConstraint } : {}),
  )

  const handleDragEnd = event => {
    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }
    const oldIndex = ids.indexOf(String(active.id))
    const newIndex = ids.indexOf(String(over.id))
    if (oldIndex >= 0 && newIndex >= 0) {
      onSortEnd(arrayMove([...items], oldIndex, newIndex))
    }
  }

  return <DndContext
    sensors={sensors}
    collisionDetection={collisionDetection}
    modifiers={[restrictToVerticalAxis]}
    onDragEnd={handleDragEnd}
  >
    <SortableContext
      items={ids}
      strategy={verticalListSortingStrategy}
    >
      {children}
    </SortableContext>
  </DndContext>
}

export const useSortableDndListItem = (id, { disabled = false } = {}) => {
  const { attributes, isDragging, listeners, setActivatorNodeRef, setNodeRef, transform, transition } = useSortable({
    id: String(id),
    disabled,
  })

  return {
    setNodeRef,
    setActivatorNodeRef,
    style: sortableItemStyle(transform, transition, isDragging),
    attributes,
    listeners,
    dragProps: {
      ...setActivatorNodeRef ? { ref: setActivatorNodeRef } : {},
      ...attributes || {},
      ...listeners || {},
      className: 'cursor-grab hover:twy-text-base-accent focus:outline-none',
    },
  }
}

export const DefaultDragFormatter = ({ row, key = 'id' }) => {
  const { dragProps } = useSortableDndListItem(row[key])
  return <ButtonIcon {...dragProps}>
    <GripVertical size={15} />
  </ButtonIcon>
}

export function removeScaleY(style = {}) {
  const { transform = '' } = style
  return { ...style, transform: transform.split(' ').filter(item => !item.startsWith('scaleY')).join(' ') }
}
